<template>
  <section class="page-wrap">
    <UiPageHero
      eyebrow="Kho tài sản"
      title="Quản lý toàn bộ thiết bị"
      description="Tra cứu, lọc trạng thái, ngừng hoạt động, chờ thanh lý và xuất báo cáo."
    >
      <template #actions>
        <UiButton
          variant="white"
          icon="refresh"
          :disabled="loading"
          @click="reloadRowsAndCounts"
        >
          {{ loading ? "Đang tải..." : "Làm mới" }}
        </UiButton>
      </template>
    </UiPageHero>

    <div class="panel-pad mb-4">
      <div class="flex flex-col gap-3">
        <div class="flex flex-wrap gap-2">
          <UiButton
            v-for="tab in tabs"
            :key="tab.key"
            icon="view"
            :variant="activeTab === tab.key ? 'primary' : 'secondary'"
            @click="setTab(tab.key)"
          >
            <span>{{ tab.label }}</span>
            <span
              class="ml-1 rounded-full px-1.5 py-0.5 text-[10px] leading-none"
              :class="
                activeTab === tab.key
                  ? 'bg-white/20 text-white'
                  : 'bg-brand-50 text-brand-700'
              "
            >
              {{ getTabCount(tab.key) }}
            </span>
          </UiButton>
        </div>
        <div
          class="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[160px_160px_220px_minmax(0,1fr)_auto] items-end"
        >
          <UiDatePicker v-model="dateFrom" class="min-w-0" label="Từ ngày" />
          <UiDatePicker v-model="dateTo" class="min-w-0" label="Đến ngày" />
          <UiSelect
            v-model.number="departmentId"
            class="min-w-0"
            @change="onDepartmentChange"
          >
            <option :value="0">Khoa/phòng</option>
            <option v-for="item in departments" :key="item.id" :value="item.id">
              {{ item.tenKhoaPhong }}
            </option>
          </UiSelect>
          <UiInput
            v-model="query"
            class="min-w-0 md:col-span-2 xl:col-span-1"
            placeholder="Tìm thiết bị, model, khoa/phòng..."
          />
          <UiButton
            class="w-full md:w-auto md:justify-self-start xl:justify-self-auto"
            icon="export"
            :disabled="!currentRowsLength || exporting"
            @click="exportExcel"
          >
            {{ exporting ? "Đang xuất..." : "Xuất Excel" }}
          </UiButton>
        </div>
      </div>
    </div>

    <div
      class="mt-4 table-wrap"
      :class="{ 'is-loading': loading }"
      v-if="isRepairTab"
    >
      <div v-if="loading" class="table-loading-overlay">
        <span class="table-spinner"></span>
        <span>Đang tải dữ liệu...</span>
      </div>
      <table class="data-table min-w-[1780px]">
        <thead>
          <tr>
            <th>STT</th>
            <th>
              <p @click="toggleSort('tenThietBi')">
                Tên thiết bị {{ sortMark("tenThietBi") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('maThietBi')">
                Mã thiết bị {{ sortMark("maThietBi") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('thoiGianBaoHong')">
                Thời gian báo hỏng {{ sortMark("thoiGianBaoHong") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('thoiGianSuaChua')">
                Thời gian sửa chữa {{ sortMark("thoiGianSuaChua") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('nguoiBaoCao')">
                Người báo cáo {{ sortMark("nguoiBaoCao") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('khoaPhongBaoCao')">
                Khoa phòng báo cáo {{ sortMark("khoaPhongBaoCao") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('noiDung')">
                Nội dung {{ sortMark("noiDung") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('nguoiSuaChua')">
                Người sửa chữa {{ sortMark("nguoiSuaChua") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('khoaPhongSuaChua')">
                Khoa phòng sửa chữa {{ sortMark("khoaPhongSuaChua") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('huongGiaiQuyet')">
                Hướng giải quyết {{ sortMark("huongGiaiQuyet") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('soTien')">
                Số tiền {{ sortMark("soTien") }}
              </p>
            </th>
            <th v-if="hasRepairTableActions">Thao tác</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          <tr
            v-for="(item, index) in paginatedRepairRows"
            :key="repairRowKey(item, index)"
            class="hover:bg-slate-50"
          >
            <td>{{ rowIndex(index) }}</td>
            <td class="font-semibold text-ink">{{ item.tenThietBi }}</td>
            <td>{{ item.maThietBi || "-" }}</td>
            <td>{{ formatDate(item.thoiGianBaoHong) }}</td>
            <td>{{ formatDate(item.thoiGianSuaChua) }}</td>
            <td>{{ item.nguoiBaoCao || "-" }}</td>
            <td>{{ item.khoaPhongBaoCao || "-" }}</td>
            <td>{{ item.noiDung || "-" }}</td>
            <td>{{ item.nguoiSuaChua || "-" }}</td>
            <td>{{ item.khoaPhongSuaChua || "-" }}</td>
            <td>{{ item.huongGiaiQuyet || "-" }}</td>
            <td>{{ formatCurrency(item.soTien) }}</td>
            <td v-if="hasRepairTableActions" class="text-right">
              <UiButton
                v-if="!item.trangThai"
                variant="secondary"
                size="sm"
                icon="edit"
                @click="openRepairTableUpdate(item)"
              >
                Cập nhật
              </UiButton>
            </td>
          </tr>
          <tr v-if="!filteredRepairRows.length">
            <td
              :colspan="repairEmptyColspan"
              class="py-10 text-center text-slate-500"
            >
              Không có dữ liệu sửa chữa phù hợp.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="mt-4 table-wrap" :class="{ 'is-loading': loading }" v-else>
      <div v-if="loading" class="table-loading-overlay">
        <span class="table-spinner"></span>
        <span>Đang tải dữ liệu...</span>
      </div>
      <table class="data-table min-w-[1900px]">
        <thead>
          <tr>
            <th>STT</th>
            <th>
              <p @click="toggleSort('tenThietBi')">
                Tên thiết bị {{ sortMark("tenThietBi") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('maThietBi')">
                Mã thiết bị {{ sortMark("maThietBi") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('namTiepNhan')">
                Năm tiếp nhận {{ sortMark("namTiepNhan") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('thoiGianCapNhat')">
                Thời gian cập nhật {{ sortMark("thoiGianCapNhat") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('giaTriTiepNhan')">
                Nguyên giá {{ sortMark("giaTriTiepNhan") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('giaTriHienTai')">
                Giá trị thời điểm tiếp nhận {{ sortMark("giaTriHienTai") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('tyLeSuDungConLai')">
                Tỷ lệ sử dụng còn lại {{ sortMark("tyLeSuDungConLai") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('yeuCauSuaChua')">
                Yêu cầu sửa chữa {{ sortMark("yeuCauSuaChua") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('khoaPhongTiepNhan')">
                Khoa phòng tiếp nhận {{ sortMark("khoaPhongTiepNhan") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('khoaPhongHienTai')">
                Khoa phòng hiện tại {{ sortMark("khoaPhongHienTai") }}
              </p>
            </th>
            <th>
              <p @click="toggleSort('khoaPhongChuyenGiao')">
                Khoa phòng chuyển giao {{ sortMark("khoaPhongChuyenGiao") }}
              </p>
            </th>
            <th v-if="hasDeviceTableActions">Thao tác</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          <tr
            v-for="(item, index) in paginatedRows"
            :key="item.id"
            class="hover:bg-slate-50"
          >
            <td>{{ rowIndex(index) }}</td>
            <td class="font-semibold text-ink">{{ item.tenThietBi }}</td>
            <td>{{ item.maThietBi || "-" }}</td>
            <td>{{ getReceiptYear(item.thoiGianTiepNhan) }}</td>
            <td>{{ formatDate(item.thoiGianCapNhat) }}</td>
            <td>{{ formatCurrency(item.giaTriTiepNhan) }}</td>
            <td>{{ formatCurrency(item.giaTriHienTai) }}</td>
            <td>{{ getRemainingUsage(item) }}</td>
            <td>
              <span :class="repairRequestClass(item)">
                {{ repairRequestLabel(item) }}
              </span>
            </td>
            <td>{{ item.khoaPhongTiepNhan || "-" }}</td>
            <td>{{ item.khoaPhongHienTai || "-" }}</td>
            <td>{{ item.khoaPhongChuyenGiao || "-" }}</td>
            <td v-if="hasDeviceTableActions" class="text-right">
              <div class="inline-flex w-max justify-end gap-2">
                <UiButton
                  variant="secondary"
                  size="sm"
                  icon="history"
                  @click="openHistory(item)"
                >
                  Lịch sử
                </UiButton>
                <UiButton
                  v-if="canTransfer"
                  variant="secondary"
                  size="sm"
                  icon="transfer"
                  @click="openTransfer(item)"
                >
                  Chuyển
                </UiButton>
                <UiButton
                  v-if="canShowRepairButton(item)"
                  variant="secondary"
                  size="sm"
                  icon="repair"
                  @click="openRepair(item)"
                >
                  Sửa chữa
                </UiButton>
                <UiButton
                  v-if="canWaitForLiquidation"
                  variant="secondary"
                  size="sm"
                  icon="send"
                  @click="openStop(item, 'liquidation')"
                >
                  Chờ thanh lý
                </UiButton>
                <UiButton
                  v-if="canLiquidate"
                  variant="secondary"
                  size="sm"
                  icon="check"
                  @click="openStop(item, 'liquidate')"
                >
                  Thanh lý
                </UiButton>
                <UiButton
                  variant="danger"
                  size="sm"
                  icon="delete"
                  @click="openDelete(item)"
                >
                  Xóa
                </UiButton>
              </div>
            </td>
          </tr>
          <tr v-if="!filteredRows.length">
            <td
              :colspan="emptyColspan"
              class="py-10 text-center text-slate-500"
            >
              Không có thiết bị phù hợp.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <UiPagination
      v-model="page"
      :total="filteredRows.length"
      :page-size="pageSize"
    />

    <UiModal v-if="modal" :size="modal === 'history' ? 'xl' : 'md'">
      <form @submit.prevent="submitModal">
        <div class="mb-4 flex items-start justify-between">
          <div>
            <p class="text-xl font-bold text-ink">{{ modalTitle }}</p>
            <p class="text-sm text-slate-500">{{ selectedName }}</p>
          </div>
          <UiButton variant="ghost" size="sm" icon="close" @click="modal = ''"
            >Đóng</UiButton
          >
        </div>

        <div
          v-if="modal === 'history'"
          class="table-wrap"
          :class="{ 'is-loading': historyLoading }"
        >
          <div v-if="historyLoading" class="table-loading-overlay">
            <span class="table-spinner"></span>
            <span>Đang tải dữ liệu...</span>
          </div>
          <table class="data-table min-w-[1500px]">
            <thead>
              <tr>
                <th>Stt</th>
                <th>Trạng thái</th>
                <th>Tên thiết bị</th>
                <th>
                  <p @click="toggleHistorySort('khoaPhongGiao')">
                    Khoa phòng chuyển {{ historySortMark("khoaPhongGiao") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('thoiGianGiao')">
                    Thời gian chuyển {{ historySortMark("thoiGianGiao") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('khoaPhongNhan')">
                    Khoa phòng nhận {{ historySortMark("khoaPhongNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('thoiGianXacNhan')">
                    Thời gian nhận {{ historySortMark("thoiGianXacNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('nguoiChuyen')">
                    Người chuyển {{ historySortMark("nguoiChuyen") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('nguoiNhan')">
                    Người nhận {{ historySortMark("nguoiNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('giaTriTiepNhan')">
                    Giá trị ban đầu {{ historySortMark("giaTriTiepNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('giaTriHienTai')">
                    Giá trị thời điểm tiếp nhận
                    {{ historySortMark("giaTriHienTai") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleHistorySort('noiDung')">
                    Nội dung {{ historySortMark("noiDung") }}
                  </p>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr
                v-for="(item, index) in sortedHistory"
                :key="historyKey(item, index)"
              >
                <td>{{ index + 1 }}</td>
                <td>
                  <span :class="historyStatusClass(item)">
                    {{ historyStatusLabel(item) }}
                  </span>
                </td>
                <td class="font-semibold text-ink">
                  {{ item.tenThietBi || selectedName || "-" }}
                </td>
                <td>{{ item.khoaPhongGiao || "-" }}</td>
                <td>{{ formatDate(item.thoiGianGiao) }}</td>
                <td>{{ item.khoaPhongNhan || "-" }}</td>
                <td>{{ formatDate(item.thoiGianXacNhan) }}</td>
                <td>{{ item.nguoiChuyen || "-" }}</td>
                <td>{{ item.nguoiNhan || "-" }}</td>
                <td>{{ formatCurrency(item.giaTriTiepNhan) }}</td>
                <td>{{ formatCurrency(item.giaTriHienTai) }}</td>
                <td>{{ historyContent(item) }}</td>
              </tr>
              <tr v-if="!history.length">
                <td colspan="12" class="py-8 text-center text-slate-500">
                  Chưa có lịch sử.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="modal === 'transfer'">
          <UiSelect
            v-model.number="transferDepartmentId"
            label="Khoa/phòng nhận"
          >
            <option :value="0">Chọn khoa/phòng</option>
            <option v-for="item in departments" :key="item.id" :value="item.id">
              {{ item.tenKhoaPhong }}
            </option>
          </UiSelect>
        </div>
        <div
          v-else-if="modal === 'repair' || modal === 'repairUpdate'"
          class="flex flex-col gap-4"
        >
          <UiSelect
            v-model.number="repairDepartmentId"
            label="Khoa/phòng sửa chữa"
          >
            <option :value="0">Chọn khoa/phòng sửa chữa</option>
            <option v-for="item in departments" :key="item.id" :value="item.id">
              {{ item.tenKhoaPhong }}
            </option>
          </UiSelect>
          <UiDatePicker v-model="dateRepair" label="Thời gian báo hỏng" />
          <UiTextarea v-model="content" label="Nội dung" required />
        </div>
        <div v-else-if="modal === 'repairComplete'" class="flex flex-col gap-4">
          <UiDatePicker
            v-model="repairCompleteDate"
            label="Thời gian sửa chữa"
            required
          />
          <UiInput
            :model-value="repairCompleteCostInput"
            label="Số tiền"
            inputmode="numeric"
            @keydown="blockNonNumericKey"
            @paste="pasteNumericOnly($event, updateRepairCompleteCost)"
            @update:modelValue="updateRepairCompleteCost"
          />
          <!--          <UiTextarea-->
          <!--            v-model="repairCompleteSolution"-->
          <!--            label="Hướng giải quyết"-->
          <!--            required-->
          <!--          />-->
          <UiSelect v-model="repairCompleteCode" label="Hướng giải quyết">
            <option value="">Chọn mã giải quyết</option>
            <option value="01">Thanh lý</option>
            <option value="02">Đã sửa</option>
          </UiSelect>
          <UiTextarea
            v-model="repairCompleteContent"
            label="Nội dung giải quyết"
            required
          />
        </div>
        <div v-else-if="modal === 'delete'">
          <p class="text-sm font-semibold text-slate-600">
            Bạn có chắc chắn muốn xóa thiết bị
            <span class="font-bold text-ink">{{ selectedName }}</span>
            không?
          </p>
        </div>
        <div v-else-if="modal">
          <UiTextarea v-model="reason" label="Lý do" required />
        </div>

        <div class="mt-4 flex justify-end gap-3">
          <UiButton variant="secondary" icon="cancel" @click="modal = ''">Hủy</UiButton>
          <UiButton
            type="submit"
            :variant="modal === 'delete' ? 'danger' : 'primary'"
            :icon="modal === 'delete' ? 'delete' : 'confirm'"
            :loading="modal === 'delete' && deletingDevice"
            :disabled="isSubmitDisabled"
            v-if="modal !== 'history'"
            >{{ modal === "delete" ? "Xác nhận xóa" : "Xác nhận" }}</UiButton
          >
        </div>
      </form>
    </UiModal>
  </section>
</template>

<script lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import QLTSRepository from "@/services/QLTSRepository";
import { ThietBi } from "@/models/thietBi";
import { KhoaPhong } from "@/models/khoaPhong";
import { LichSuBanGiao } from "@/models/lichSuBanGiao";
import {
  LichSuSuaChuaCreate,
  LichSuSuaChuaHoanThanhUpdate,
  LichSuSuaChuaUpdate,
} from "@/models/lichSuSuaChuaCreate";
import {
  formatCurrency,
  formatDate,
  formatIntegerInput,
  normalizeText,
  parseIntegerInput,
  blockNonNumericKey,
  pasteNumericOnly,
  toTimestamp,
} from "@/utils/format";
import { getErrorMessage, useToast } from "@/utils/toast";
import {
  compareSortValues,
  nextSortState,
  sortIndicator,
  SortState,
} from "@/utils/tableSort";
import { LichSuSuaChuaDetail } from "@/models/lichSuSuaChuaDetail";

type TabKey =
  | "all"
  | "active"
  | "expired"
  | "transfer"
  | "stopped"
  | "liquidation"
  | "department"
  | "repair"
  | "repairRequest";
type ModalKey =
  | ""
  | "history"
  | "transfer"
  | "repair"
  | "repairUpdate"
  | "repairComplete"
  | "stop"
  | "liquidation"
  | "liquidate"
  | "delete";

export default {
  setup() {
    const tabs = [
      { key: "all" as TabKey, label: "Tất cả" },
      { key: "active" as TabKey, label: "Hoạt động" },
      { key: "expired" as TabKey, label: "Hết hạn" },
      { key: "transfer" as TabKey, label: "Chuyển giao" },
      { key: "liquidation" as TabKey, label: "Chờ thanh lý" },
      { key: "stopped" as TabKey, label: "Đã thanh lý" },
      { key: "department" as TabKey, label: "Theo khoa" },
      { key: "repair" as TabKey, label: "Sửa chữa" },
      { key: "repairRequest" as TabKey, label: "Yêu cầu sửa chữa" },
    ];
    const activeTab = ref<TabKey>("all");
    const rows = ref<ThietBi[]>([]);
    const departments = ref<KhoaPhong[]>([]);
    const { showToast } = useToast();
    const loading = ref(false);
    const exporting = ref(false);
    const query = ref("");
    const dateFrom = ref("");
    const dateTo = ref("");
    const departmentId = ref(0);
    const modal = ref<ModalKey>("");
    const selected = ref<ThietBi | null>(null);
    const selectedRepair = ref<LichSuSuaChuaDetail | null>(null);
    const history = ref<LichSuBanGiao[]>([]);
    const historyLoading = ref(false);
    const sortState = ref<SortState>({ key: "", direction: "asc" });
    const historySortState = ref<SortState>({ key: "", direction: "asc" });
    const page = ref(1);
    const pageSize = 10;
    const transferDepartmentId = ref(0);
    const repairDepartmentId = ref(0);
    const dateRepair = ref<string | Date>(new Date());
    const reason = ref("");
    const content = ref("");
    const repairCompleteDate = ref("");
    const repairCompleteCost = ref(0);
    const repairCompleteCostInput = ref("");
    const repairCompleteSolution = ref("");
    const repairCompleteCode = ref("");
    const repairCompleteContent = ref("");
    const deletingDevice = ref(false);
    const listRepair = ref([] as LichSuSuaChuaDetail[]);
    const repairTabKeys: TabKey[] = ["repair", "repairRequest"];
    const isRepairTab = computed(() => repairTabKeys.includes(activeTab.value));
    const tabCounts = ref<Record<TabKey, number>>({
      all: 0,
      active: 0,
      expired: 0,
      transfer: 0,
      stopped: 0,
      liquidation: 0,
      department: 0,
      repair: 0,
      repairRequest: 0,
    });

    const range = () => [
      toTimestamp(dateFrom.value),
      toTimestamp(dateTo.value || new Date()),
    ];
    const createTabRequest = (key: TabKey) => {
      const [t1, t2] = range();
      return key === "active"
        ? QLTSRepository.getListThietBiHoatDong()
        : key === "expired"
        ? QLTSRepository.getListThietBiHetHan(t1, t2)
        : key === "transfer"
        ? QLTSRepository.getListThietBiChuyenGiao(t1, t2)
        : key === "stopped"
        ? QLTSRepository.getListThietBiNgungHoatDong(t1, t2)
        : key === "liquidation"
        ? QLTSRepository.getListThietBiChoThanhLy(t1, t2)
        : key === "department" && departmentId.value
        ? QLTSRepository.getListThietBiTheoKhoa(departmentId.value, t1, t2)
        : key === "repair"
        ? QLTSRepository.getListLichSuSuaChua()
        : key === "repairRequest"
        ? QLTSRepository.getListLichSuSuaChuaYeuCau()
        : QLTSRepository.getListThietBiAll(t1, t2);
    };
    const updateTabCount = (key: TabKey, data: unknown) => {
      tabCounts.value[key] = Array.isArray(data) ? data.length : 0;
    };
    const getTabCount = (key: TabKey) => tabCounts.value[key] || 0;
    const refreshTabCounts = (excludedTab?: TabKey) => {
      Promise.allSettled(
        tabs
          .filter((tab) => tab.key !== excludedTab)
          .map((tab) =>
            createTabRequest(tab.key).then((res) => ({
              key: tab.key,
              data: res.data,
            })),
          ),
      )
        .then((results) => {
          results.forEach((result) => {
            if (result.status === "fulfilled") {
              updateTabCount(result.value.key, result.value.data);
            }
          });
        })
        .catch(() => undefined);
    };
    const loadRows = () => {
      loading.value = true;
      const requestTab = activeTab.value;
      const request = createTabRequest(requestTab);
      request
        .then((res) => {
          updateTabCount(requestTab, res.data);
          if (isRepairTab.value) {
            listRepair.value = res.data || [];
            rows.value = [];
          } else {
            rows.value = res.data || [];
            listRepair.value = [];
          }
        })
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được danh sách thiết bị."),
            "error",
          );
        })
        .finally(() => (loading.value = false));
    };
    const reloadRowsAndCounts = () => {
      loadRows();
      refreshTabCounts(activeTab.value);
    };

    const setTab = (key: TabKey) => {
      activeTab.value = key;
      rows.value = [];
      listRepair.value = [];
      loadRows();
      refreshTabCounts(key);
    };
    const onDepartmentChange = () => {
      reloadRowsAndCounts();
    };

    const filteredRows = computed(() => {
      const term = normalizeText(query.value);
      return rows.value.filter((item) => {
        const matchesDepartment =
          !departmentId.value ||
          item.idKhoaPhongHienTai === departmentId.value ||
          item.idKhoaPhongTiepNhan === departmentId.value ||
          item.idKhoaPhongChuyenGiao === departmentId.value;
        if (!matchesDepartment) return false;
        if (!term) return true;
        return [
          item.tenThietBi,
          item.model,
          item.loaiThietBi,
          item.khoaPhongHienTai,
          item.khoaPhongTiepNhan,
        ]
          .map(normalizeText)
          .some((value) => value.includes(term));
      });
    });

    const filteredRepairRows = computed(() => {
      const term = normalizeText(query.value);
      return listRepair.value.filter((item) => {
        const matchesDepartment =
          !departmentId.value ||
          item.idKhoaPhongBaoCao === departmentId.value ||
          item.idNoiSuaChua === departmentId.value;
        if (!matchesDepartment) return false;
        if (!term) return true;
        return [
          item.tenThietBi,
          item.maThietBi,
          item.nguoiBaoCao,
          item.khoaPhongBaoCao,
          item.noiDung,
          item.nguoiSuaChua,
          item.khoaPhongSuaChua,
          item.huongGiaiQuyet,
        ]
          .map(normalizeText)
          .some((value) => value.includes(term));
      });
    });
    const getSortValue = (item: ThietBi, key: string) => {
      const map: Record<string, unknown> = {
        tenThietBi: item.tenThietBi,
        maThietBi: item.maThietBi,
        namTiepNhan: getReceiptYear(item.thoiGianTiepNhan),
        thoiGianCapNhat: item.thoiGianCapNhat,
        giaTriTiepNhan: item.giaTriTiepNhan,
        giaTriHienTai: item.giaTriHienTai,
        tyLeSuDungConLai: (item as any).tyLeSuDungConLai,
        yeuCauSuaChua: item.yeuCauSuaChua,
        khoaPhongTiepNhan: item.khoaPhongTiepNhan,
        khoaPhongHienTai: item.khoaPhongHienTai,
        khoaPhongChuyenGiao: item.khoaPhongChuyenGiao,
      };
      return map[key];
    };
    const sortedRows = computed(() => {
      if (!sortState.value.key) return filteredRows.value;
      return [...filteredRows.value].sort((first, second) =>
        compareSortValues(
          getSortValue(first, sortState.value.key),
          getSortValue(second, sortState.value.key),
          sortState.value.direction,
        ),
      );
    });

    const getRepairSortValue = (item: LichSuSuaChuaDetail, key: string) => {
      const map: Record<string, unknown> = {
        tenThietBi: item.tenThietBi,
        maThietBi: item.maThietBi,
        thoiGianBaoHong: item.thoiGianBaoHong,
        thoiGianSuaChua: item.thoiGianSuaChua,
        nguoiBaoCao: item.nguoiBaoCao,
        khoaPhongBaoCao: item.khoaPhongBaoCao,
        noiDung: item.noiDung,
        nguoiSuaChua: item.nguoiSuaChua,
        khoaPhongSuaChua: item.khoaPhongSuaChua,
        huongGiaiQuyet: item.huongGiaiQuyet,
        soTien: item.soTien,
      };
      return map[key];
    };
    const sortedRepairRows = computed(() => {
      if (!sortState.value.key) return filteredRepairRows.value;
      return [...filteredRepairRows.value].sort((first, second) =>
        compareSortValues(
          getRepairSortValue(first, sortState.value.key),
          getRepairSortValue(second, sortState.value.key),
          sortState.value.direction,
        ),
      );
    });
    const paginatedRows = computed(() => {
      const start = (page.value - 1) * pageSize;
      return sortedRows.value.slice(start, start + pageSize);
    });
    const paginatedRepairRows = computed(() => {
      const start = (page.value - 1) * pageSize;
      return sortedRepairRows.value.slice(start, start + pageSize);
    });
    const hasDeviceTableActions = computed(
      () => paginatedRows.value.length > 0,
    );
    const hasRepairTableActions = computed(() =>
      paginatedRepairRows.value.some((item) => !item.trangThai),
    );
    const emptyColspan = computed(() =>
      hasDeviceTableActions.value ? 13 : 12,
    );
    const repairEmptyColspan = computed(() =>
      hasRepairTableActions.value ? 13 : 12,
    );
    watch([query, activeTab, departmentId, dateFrom, dateTo], () => {
      page.value = 1;
    });
    watch([dateFrom, dateTo], () => {
      reloadRowsAndCounts();
    });
    const currentRowsLength = computed(() =>
      isRepairTab.value
        ? filteredRepairRows.value.length
        : filteredRows.value.length,
    );
    watch(currentRowsLength, () => {
      const maxPage = Math.max(
        1,
        Math.ceil(currentRowsLength.value / pageSize),
      );
      if (page.value > maxPage) page.value = maxPage;
    });

    const statusText = (item: ThietBi) => {
      if (activeTab.value === "liquidation") return "Chờ thanh lý";
      if (item.hoatDong === false || activeTab.value === "stopped")
        return "Ngừng hoạt động";
      if (item.tuChoi) return "Từ chối";
      return "Hoạt động";
    };
    const statusClass = (item: ThietBi) => [
      "status-pill",
      item.hoatDong === false || activeTab.value === "stopped"
        ? "bg-slate-100 text-slate-700"
        : item.tuChoi
        ? "bg-rose-50 text-rose-700"
        : "bg-emerald-50 text-emerald-700",
    ];
    const rowIndex = (index: number) => (page.value - 1) * pageSize + index + 1;
    const repairRowKey = (item: LichSuSuaChuaDetail, index: number) =>
      `${item.idSuaChua || item.idBaoCao || item.idThietBi}-${index}`;
    const getReceiptYear = (value?: string | Date | number | null) => {
      if (!value) return "-";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "-" : String(date.getFullYear());
    };
    const toDateInputValue = (value?: string | Date | number | null) => {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toISOString().slice(0, 10);
    };
    const updateRepairCompleteCost = (value: string | number) => {
      repairCompleteCost.value = parseIntegerInput(value);
      repairCompleteCostInput.value = formatIntegerInput(value);
    };
    const getRemainingUsage = (item: ThietBi) => {
      const value = (item as any).tyLeSuDungConLai;
      if (value === null || value === undefined || value === "") return "-";
      return `${Number(value).toLocaleString("vi-VN", {
        maximumFractionDigits: 2,
      })}%`;
    };
    const repairRequestLabel = (item: ThietBi) =>
      item.yeuCauSuaChua ? "Có" : "Không";
    const repairRequestClass = (item: ThietBi) => [
      "status-pill",
      item.yeuCauSuaChua
        ? "bg-amber-50 text-amber-700"
        : "bg-slate-100 text-slate-700",
    ];
    const toggleSort = (key: string) => {
      sortState.value = nextSortState(sortState.value, key);
      page.value = 1;
    };
    const sortMark = (key: string) => sortIndicator(sortState.value, key);
    const exportOptionId = computed(() => {
      if (activeTab.value === "active") return 1;
      if (activeTab.value === "expired") return 2;
      if (["stopped", "liquidation"].includes(activeTab.value)) return 3;
      return 0;
    });
    const downloadExcelFile = (blob: Blob, fileName: string) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const extension = fileName.includes(".")
        ? fileName.slice(fileName.lastIndexOf("."))
        : ".xlsx";
      link.href = url;
      link.download = `DanhSachThongKe${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };
    const exportExcel = () => {
      if (!currentRowsLength.value) return;
      if (isRepairTab.value) {
        showToast("Chưa hỗ trợ xuất Excel cho tab sửa chữa.", "info");
        return;
      }
      exporting.value = true;
      QLTSRepository.exportBaoCao(filteredRows.value, exportOptionId.value)
        .then((res) => {
          const fileName = String(res.data || "");
          if (!fileName) throw new Error("API không trả về tên file export.");
          return QLTSRepository.downloadListChart(fileName).then(
            (downloadRes) => {
              downloadExcelFile(downloadRes.data, fileName);
              showToast("Đã xuất file Excel.", "success");
            },
          );
        })
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không xuất được file Excel."),
            "error",
          );
        })
        .finally(() => (exporting.value = false));
    };
    const canTransfer = computed(() =>
      ["all", "active"].includes(activeTab.value),
    );
    const canRepair = computed(() =>
      ["all", "active"].includes(activeTab.value),
    );
    const canShowRepairButton = (item: ThietBi) =>
      canRepair.value && !item.yeuCauSuaChua;
    const canWaitForLiquidation = computed(() => activeTab.value === "all");
    const canLiquidate = computed(() => activeTab.value === "liquidation");
    const historyKey = (item: LichSuBanGiao, index: number) =>
      `${item.idThietBi}-${item.idKhoaPhongGiao}-${item.idKhoaPhongNhan}-${index}`;
    const historyStatusLabel = (item: LichSuBanGiao) =>
      item.tuChoi ? "Từ chối" : item.thoiGianXacNhan ? "Đã nhận" : "Chờ nhận";
    const historyStatusClass = (item: LichSuBanGiao) => [
      "status-pill",
      item.tuChoi
        ? "bg-rose-50 text-rose-700"
        : item.thoiGianXacNhan
        ? "bg-emerald-50 text-emerald-700"
        : "bg-brand-50 text-brand-700",
    ];
    const historyContent = (item: LichSuBanGiao) =>
      item.lyDoTuChoi ||
      (item.tuChoi ? "Từ chối bàn giao" : "Bàn giao thiết bị");
    const getHistorySortValue = (item: LichSuBanGiao, key: string) => {
      const map: Record<string, unknown> = {
        khoaPhongGiao: item.khoaPhongGiao,
        thoiGianGiao: item.thoiGianGiao,
        khoaPhongNhan: item.khoaPhongNhan,
        thoiGianXacNhan: item.thoiGianXacNhan,
        nguoiChuyen: item.nguoiChuyen,
        nguoiNhan: item.nguoiNhan,
        giaTriTiepNhan: item.giaTriTiepNhan,
        giaTriHienTai: item.giaTriHienTai,
        noiDung: historyContent(item),
      };
      return map[key];
    };
    const sortedHistory = computed(() => {
      if (!historySortState.value.key) return history.value;
      return [...history.value].sort((first, second) =>
        compareSortValues(
          getHistorySortValue(first, historySortState.value.key),
          getHistorySortValue(second, historySortState.value.key),
          historySortState.value.direction,
        ),
      );
    });
    const toggleHistorySort = (key: string) => {
      historySortState.value = nextSortState(historySortState.value, key);
    };
    const historySortMark = (key: string) =>
      sortIndicator(historySortState.value, key);

    const openHistory = (item: ThietBi) => {
      selected.value = item;
      selectedRepair.value = null;
      history.value = [];
      modal.value = "history";
      historyLoading.value = true;
      QLTSRepository.getListLichSuBanGiaoById(item.id)
        .then((res) => (history.value = res.data || []))
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được lịch sử bàn giao."),
            "error",
          );
        })
        .finally(() => (historyLoading.value = false));
    };

    const openTransfer = (item: ThietBi) => {
      selected.value = item;
      selectedRepair.value = null;
      transferDepartmentId.value = 0;
      modal.value = "transfer";
    };
    const openRepair = (item: ThietBi) => {
      selected.value = item;
      selectedRepair.value = null;
      repairDepartmentId.value = 0;
      dateRepair.value = toDateInputValue(new Date());
      content.value = "";
      modal.value = "repair";
    };
    const openRepairUpdate = (item: LichSuSuaChuaDetail) => {
      selected.value = null;
      selectedRepair.value = item;
      repairDepartmentId.value = item.idNoiSuaChua || 0;
      dateRepair.value = toDateInputValue(item.thoiGianBaoHong);
      content.value = item.noiDung || "";
      modal.value = "repairUpdate";
    };
    const openRepairComplete = (item: LichSuSuaChuaDetail) => {
      selected.value = null;
      selectedRepair.value = item;
      repairCompleteDate.value = toDateInputValue(
        item.thoiGianSuaChua || new Date(),
      );
      updateRepairCompleteCost(item.soTien || 0);
      repairCompleteSolution.value = item.huongGiaiQuyet || "";
      repairCompleteCode.value = item.maGiaiQuyet || "";
      repairCompleteContent.value = item.noiDungGiaiQuyet || "";
      modal.value = "repairComplete";
    };
    const openRepairTableUpdate = (item: LichSuSuaChuaDetail) => {
      if (activeTab.value === "repairRequest") {
        openRepairComplete(item);
        return;
      }
      openRepairUpdate(item);
    };
    const openStop = (item: ThietBi, type: "stop" | "liquidation") => {
      selected.value = item;
      selectedRepair.value = null;
      reason.value = "";
      modal.value = type;
    };
    const openDelete = (item: ThietBi) => {
      selected.value = item;
      selectedRepair.value = null;
      modal.value = "delete";
    };
    const isSubmitDisabled = computed(() => {
      if (modal.value === "delete") return deletingDevice.value;
      if (modal.value === "transfer") return !transferDepartmentId.value;
      if (modal.value === "repair" || modal.value === "repairUpdate")
        return !repairDepartmentId.value || !content.value || !dateRepair.value;
      if (modal.value === "repairComplete")
        return (
          !repairCompleteDate.value ||
          // !repairCompleteSolution.value ||
          !repairCompleteCode.value ||
          !repairCompleteContent.value
        );
      return !reason.value;
    });
    const submitModal = () => {
      if (
        !["repairUpdate", "repairComplete"].includes(modal.value) &&
        !selected.value
      )
        return;
      if (modal.value === "transfer") {
        if (!selected.value) return;
        QLTSRepository.tranferThietBi(
          selected.value.id,
          transferDepartmentId.value,
        )
          .then(() => {
            showToast("Đã chuyển thiết bị.", "success");
            modal.value = "";
            reloadRowsAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không chuyển được thiết bị."),
              "error",
            );
          });
      } else if (modal.value === "repair") {
        if (!selected.value) return;
        const payload: LichSuSuaChuaCreate = {
          idThietBi: selected.value.id,
          idNoiSuaChua: repairDepartmentId.value,
          thoiGianBaoHong: dateRepair.value,
          noiDung: content.value,
        };
        QLTSRepository.createLichSuSuaChua(payload)
          .then(() => {
            showToast("Đã gửi yêu cầu sửa chữa.", "success");
            modal.value = "";
            reloadRowsAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không gửi được yêu cầu sửa chữa."),
              "error",
            );
          });
      } else if (modal.value === "repairUpdate") {
        if (!selectedRepair.value) return;
        const payload: LichSuSuaChuaUpdate = {
          id: selectedRepair.value.id || 0,
          idThietBi: selectedRepair.value.idThietBi,
          idNoiSuaChua: repairDepartmentId.value,
          thoiGianBaoHong: dateRepair.value,
          noiDung: content.value,
        };
        QLTSRepository.updateLichSuSuaChua(payload)
          .then(() => {
            showToast("Đã cập nhật yêu cầu sửa chữa.", "success");
            modal.value = "";
            reloadRowsAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không cập nhật được yêu cầu sửa chữa."),
              "error",
            );
          });
      } else if (modal.value === "repairComplete") {
        if (!selectedRepair.value) return;
        const payload: LichSuSuaChuaHoanThanhUpdate = {
          id:
            selectedRepair.value.id ||
            selectedRepair.value.idSuaChua ||
            selectedRepair.value.idBaoCao ||
            0,
          thoiGianSuaChua: repairCompleteDate.value,
          trangThai: true,
          soTien: Number(repairCompleteCost.value || 0),
          huongGiaiQuyet:
            repairCompleteCode.value == "01" ? "Thanh lý" : "Đã sửa",
          maGiaiQuyet: repairCompleteCode.value,
          noiDungGiaiQuyet: repairCompleteContent.value,
        };
        QLTSRepository.updateLichSuSuaChuaHoanThanh(payload)
          .then(() => {
            showToast("Đã cập nhật yêu cầu sửa chữa.", "success");
            modal.value = "";
            reloadRowsAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không cập nhật được yêu cầu sửa chữa."),
              "error",
            );
          });
      } else if (modal.value === "stop") {
        if (!selected.value) return;
        QLTSRepository.deactiveThietBi({
          id: selected.value.id,
          lyDoNgung: reason.value,
        } as any)
          .then(() => {
            showToast("Đã ngừng hoạt động thiết bị.", "success");
            modal.value = "";
            reloadRowsAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không ngừng hoạt động được thiết bị."),
              "error",
            );
          });
      } else if (modal.value === "liquidation") {
        if (!selected.value) return;
        QLTSRepository.waitingForLiquidationThietBi({
          id: selected.value.id,
          lyDoNgung: reason.value,
        } as any)
          .then(() => {
            showToast("Đã đưa thiết bị vào chờ thanh lý.", "success");
            modal.value = "";
            reloadRowsAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không đưa thiết bị vào chờ thanh lý được."),
              "error",
            );
          });
      } else if (modal.value === "liquidate") {
        if (!selected.value) return;
        QLTSRepository.deactiveThietBi({
          id: selected.value.id,
          lyDoNgung: reason.value,
        } as any)
          .then(() => {
            showToast("Đã thanh lý thiết bị.", "success");
            modal.value = "";
            reloadRowsAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không thanh lý được thiết bị."),
              "error",
            );
          });
      } else if (modal.value === "delete") {
        if (!selected.value) return;
        deletingDevice.value = true;
        QLTSRepository.deleteThietBi(selected.value.id)
          .then(() => {
            showToast("Đã xóa thiết bị.", "success");
            modal.value = "";
            reloadRowsAndCounts();
          })
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không xóa được thiết bị."),
              "error",
            );
          })
          .finally(() => (deletingDevice.value = false));
      }
    };
    const modalTitle = computed(() =>
      modal.value === "history"
        ? "Lịch sử bàn giao"
        : modal.value === "transfer"
        ? "Chuyển thiết bị"
        : modal.value === "repair"
        ? "Yêu cầu sửa chữa"
        : modal.value === "repairUpdate" || modal.value === "repairComplete"
        ? "Cập nhật yêu cầu sửa chữa"
        : modal.value === "liquidate"
        ? "Thanh lý thiết bị"
        : modal.value === "stop"
        ? "Ngừng hoạt động"
        : modal.value === "delete"
        ? "Xóa thiết bị"
        : "Đưa vào chờ thanh lý",
    );
    const selectedName = computed(() =>
      selectedRepair.value
        ? selectedRepair.value.tenThietBi
        : selected.value
        ? selected.value.tenThietBi
        : "",
    );
    onMounted(() => {
      QLTSRepository.getListKhoaPhong()
        .then((res) => (departments.value = res.data || []))
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được danh sách khoa/phòng."),
            "error",
          );
        });
      loadRows();
      refreshTabCounts(activeTab.value);
    });

    return {
      tabs,
      activeTab,
      isRepairTab,
      getTabCount,
      setTab,
      onDepartmentChange,
      rows,
      filteredRows,
      filteredRepairRows,
      sortedRows,
      paginatedRows,
      paginatedRepairRows,
      hasDeviceTableActions,
      hasRepairTableActions,
      emptyColspan,
      repairEmptyColspan,
      page,
      pageSize,
      departments,
      loading,
      exporting,
      query,
      dateFrom,
      dateTo,
      departmentId,
      loadRows,
      reloadRowsAndCounts,
      statusText,
      statusClass,
      formatDate,
      formatCurrency,
      rowIndex,
      repairRowKey,
      getReceiptYear,
      getRemainingUsage,
      repairRequestLabel,
      repairRequestClass,
      toggleSort,
      sortMark,
      exportExcel,
      canTransfer,
      canRepair,
      canShowRepairButton,
      canWaitForLiquidation,
      canLiquidate,
      history,
      historyLoading,
      sortedHistory,
      historyKey,
      historyStatusLabel,
      historyStatusClass,
      historyContent,
      toggleHistorySort,
      historySortMark,
      modal,
      selected,
      selectedRepair,
      selectedName,
      modalTitle,
      transferDepartmentId,
      repairDepartmentId,
      reason,
      isSubmitDisabled,
      openHistory,
      openTransfer,
      openRepair,
      openRepairUpdate,
      openRepairComplete,
      openRepairTableUpdate,
      openStop,
      openDelete,
      submitModal,
      dateRepair,
      content,
      repairCompleteDate,
      repairCompleteCost,
      repairCompleteCostInput,
      updateRepairCompleteCost,
      blockNonNumericKey,
      pasteNumericOnly,
      repairCompleteSolution,
      repairCompleteCode,
      repairCompleteContent,
      deletingDevice,
      currentRowsLength,
    };
  },
};
</script>
